(() => {
  'use strict';

  const DIALOG_SELECTOR = '[data-service-panel]';
  const LINK_SELECTOR = '[data-service-panel-link]';
  const pageCache = new Map();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const mount = dialog => {
    if (!dialog || dialog.dataset.servicePanelMounted === 'true') return;
    dialog.dataset.servicePanelMounted = 'true';

    const titleEl = dialog.querySelector('[data-service-panel-title]');
    const bodyEl = dialog.querySelector('[data-service-panel-body]');
    const contentEl = dialog.querySelector('[data-service-panel-content]');
    const statusEl = dialog.querySelector('[data-service-panel-status]');
    const fullEl = dialog.querySelector('[data-service-panel-full]');
    const fullLabelEl = dialog.querySelector('[data-service-panel-full-label]');
    const closeEl = dialog.querySelector('[data-service-panel-close]');
    if (!titleEl || !bodyEl || !contentEl || !statusEl || !fullEl || !closeEl) return;
    const defaultFullLabel = fullLabelEl ? fullLabelEl.textContent : '';

    const copy = {
      loading: dialog.dataset.loading || 'Loading…',
      error: dialog.dataset.error || 'We couldn’t load this section.',
      retry: dialog.dataset.retry || 'Try again'
    };
    const state = { controller: null, currentUrl: '', focusKey: '', trigger: null, requestId: 0 };

    const setTitle = value => {
      const next = typeof value === 'string' ? value.trim() : '';
      if (next) titleEl.textContent = next;
    };

    const readWarranty = () => {
      const details = Array.from(document.querySelectorAll('details')).find(item => {
        const summary = item.querySelector('summary');
        return summary && /3-year warranty/i.test(summary.textContent || '');
      });
      const body = details && details.querySelector('.rte');
      return body ? body.innerHTML : '';
    };

    const setStatus = (message, isError = false) => {
      statusEl.classList.toggle('is-error', isError);
      statusEl.textContent = message || '';
      statusEl.hidden = !message;
    };

    const absoluteSrcset = (value, base) => value.split(',').map(part => {
      const bits = part.trim().split(/\s+/);
      if (!bits[0]) return '';
      try { bits[0] = new URL(bits[0], base).href; } catch (_) { return ''; }
      return bits.join(' ');
    }).filter(Boolean).join(', ');

    const extract = (html, url) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const main = doc.querySelector('#MainContent') || doc.querySelector('main');
      if (!main) throw new Error('Service page content was not found.');

      const heading = main.querySelector('h1');
      const title = ((heading && heading.textContent) || doc.title || '').replace(/\s*[—|]\s*ThrustMate.*$/i, '').replace(/\s+/g, ' ').trim();

      main.querySelectorAll('script,style,link,iframe,noscript,header,footer,form,nav,aside,.cv-toc,.contact-form-panel,[data-cookie-consent],[hidden]').forEach(node => node.remove());
      main.querySelectorAll('h1').forEach(node => node.remove());
      main.querySelectorAll('[data-tm-reveal],[data-tm-reveal-state]').forEach(node => {
        node.removeAttribute('data-tm-reveal');
        node.removeAttribute('data-tm-reveal-state');
      });
      main.querySelectorAll('*').forEach(node => {
        if (!/^(IMG|VIDEO|SOURCE|PICTURE)$/.test(node.tagName)) node.removeAttribute('style');
        node.removeAttribute('id');
        Array.from(node.attributes).forEach(attribute => {
          if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
        });
      });

      main.querySelectorAll('a[href]').forEach(link => {
        const raw = link.getAttribute('href');
        if (!raw || /^javascript:/i.test(raw)) { link.removeAttribute('href'); return; }
        try {
          const target = new URL(raw, url);
          link.href = target.href;
          if (target.origin !== window.location.origin) {
            link.target = '_blank';
            link.rel = 'noopener';
          }
        } catch (_) { link.removeAttribute('href'); }
      });
      main.querySelectorAll('img[src]').forEach(image => {
        try { image.src = new URL(image.getAttribute('src'), url).href; } catch (_) { return; }
        image.loading = 'lazy';
        image.decoding = 'async';
      });
      main.querySelectorAll('img[srcset],source[srcset]').forEach(node => {
        const srcset = node.getAttribute('srcset');
        if (srcset) node.setAttribute('srcset', absoluteSrcset(srcset, url));
      });
      main.querySelectorAll('source[src]').forEach(node => {
        try { node.src = new URL(node.getAttribute('src'), url).href; } catch (_) {}
      });
      main.querySelectorAll('video[poster]').forEach(node => {
        try { node.poster = new URL(node.getAttribute('poster'), url).href; } catch (_) {}
      });

      return { html: main.innerHTML, title };
    };

    const fetchPage = async (url, signal) => {
      if (pageCache.has(url)) return pageCache.get(url);
      let response;
      try {
        response = await fetch(url, { signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        throw new Error('Network request failed.');
      }
      if (!response.ok) throw new Error('Service page returned ' + response.status + '.');
      const page = extract(await response.text(), url);
      pageCache.set(url, page);
      return page;
    };

    const focusTarget = () => {
      if (!state.focusKey) { bodyEl.scrollTop = 0; return; }
      let target = null;
      if (state.focusKey === 'discreet') {
        target = Array.from(contentEl.querySelectorAll('h2,h3,h4')).find(heading => /discreet/i.test(heading.textContent || ''));
      }
      if (!target) { bodyEl.scrollTop = 0; return; }
      const top = target.getBoundingClientRect().top - bodyEl.getBoundingClientRect().top + bodyEl.scrollTop - 10;
      bodyEl.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };

    const showError = () => {
      setStatus(copy.error, true);
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'service-panel__retry';
      retry.textContent = copy.retry;
      retry.addEventListener('click', () => load(state.currentUrl, false));
      statusEl.append(retry);
    };

    const load = async (url, updateTitle) => {
      const targetUrl = new URL(url, window.location.href);
      targetUrl.hash = '';
      state.currentUrl = targetUrl.href;
      fullEl.href = targetUrl.href;
      if (fullLabelEl) fullLabelEl.textContent = defaultFullLabel;
      state.controller?.abort();
      const controller = new AbortController();
      state.controller = controller;
      const requestId = ++state.requestId;

      bodyEl.setAttribute('aria-busy', 'true');
      contentEl.replaceChildren();
      setStatus(copy.loading);
      bodyEl.scrollTop = 0;

      try {
        const page = await fetchPage(targetUrl.href, controller.signal);
        if (state.controller !== controller || requestId !== state.requestId) return;
        if (updateTitle && page.title) setTitle(page.title);
        contentEl.innerHTML = page.html;
        contentEl.scrollTop = 0;
        bodyEl.removeAttribute('aria-busy');
        setStatus('');
        state.controller = null;
        if (updateTitle) {
          titleEl.setAttribute('tabindex', '-1');
          titleEl.focus({ preventScroll: true });
        } else if (!dialog.contains(document.activeElement)) {
          closeEl.focus({ preventScroll: true });
        }
        requestAnimationFrame(focusTarget);
      } catch (error) {
        if (error.name === 'AbortError' || state.controller !== controller || requestId !== state.requestId) return;
        bodyEl.removeAttribute('aria-busy');
        contentEl.replaceChildren();
        showError();
        state.controller = null;
      }
    };

    const open = link => {
      const targetUrl = new URL(link.href, window.location.href);
      if (targetUrl.origin !== window.location.origin) return false;
      if (typeof dialog.showModal !== 'function') return false;

      state.focusKey = link.dataset.serviceFocus || '';
      state.trigger = link;
      setTitle(link.dataset.serviceTitle || '');
      if (fullLabelEl) fullLabelEl.textContent = link.dataset.serviceFooterLabel || defaultFullLabel;

      if (!dialog.open) {
        document.querySelectorAll('dialog[open]').forEach(other => {
          if (other !== dialog) other.close();
        });
        try { dialog.showModal(); } catch (_) { window.location.assign(targetUrl.href); return true; }
        document.documentElement.classList.add('service-panel-open');
        closeEl.focus({ preventScroll: true });
      }

      const localWarranty = link.dataset.serviceSource === 'warranty' ? readWarranty() : '';
      if (localWarranty) {
        state.controller?.abort();
        state.controller = null;
        state.currentUrl = targetUrl.href;
        fullEl.href = targetUrl.href;
        contentEl.innerHTML = localWarranty;
        setStatus('');
        bodyEl.removeAttribute('aria-busy');
        bodyEl.scrollTop = 0;
        return true;
      }

      load(targetUrl.href, false);
      return true;
    };

    document.addEventListener('click', event => {
      const link = event.target.closest(LINK_SELECTOR);
      if (!link) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      if (typeof dialog.showModal !== 'function') return;
      const targetUrl = new URL(link.href, window.location.href);
      if (targetUrl.origin !== window.location.origin) return;
      event.preventDefault();
      open(link);
    });

    contentEl.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('download') || link.target === '_blank') return;
      let targetUrl;
      try { targetUrl = new URL(link.getAttribute('href'), state.currentUrl || window.location.href); } catch (_) { return; }
      if (targetUrl.origin !== window.location.origin) return;
      if (targetUrl.hash) {
        let hashTarget = null;
        try { hashTarget = contentEl.querySelector(targetUrl.hash); } catch (_) {}
        if (hashTarget) {
          event.preventDefault();
          hashTarget.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
          return;
        }
      }
      event.preventDefault();
      state.focusKey = '';
      load(targetUrl.href, true);
    });

    closeEl.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      state.controller?.abort();
      state.controller = null;
      if (!dialog.open) document.documentElement.classList.remove('service-panel-open');
      const trigger = state.trigger;
      state.trigger = null;
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const focusables = Array.from(dialog.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])'))
        .filter(element => element.tabIndex >= 0 && element.getClientRects().length);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  document.querySelectorAll(DIALOG_SELECTOR).forEach(mount);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll?.(DIALOG_SELECTOR).forEach(mount);
  });
})();
